import React, { useState, useCallback } from 'react';
// import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import './RedditBuzz.css';

// getRedditTrending is not available in the current API
const getRedditTrending = async () => ({ list: [] });

const MIN_SIZE = 44;
const MAX_SIZE = 86;
const MAX_ITEMS = 15;

const computeSize = (mentions, minM, maxM) => {
  if (maxM === minM) return (MIN_SIZE + MAX_SIZE) / 2;
  const ratio = (Math.sqrt(mentions) - Math.sqrt(minM)) / (Math.sqrt(maxM) - Math.sqrt(minM));
  return Math.round(MIN_SIZE + ratio * (MAX_SIZE - MIN_SIZE));
};

// 결정론적 초기 jitter (인덱스+크기 기반)
const initX = (idx)       => Math.round(Math.sin(idx * 1.7 + 1) * 5);
const initY = (idx, size) => Math.round(Math.sin(idx * 2.1 + size * 0.05) * 16);

const BubbleCloud = ({ items, onBubbleClick }) => {
  const [positions, setPositions] = useState(() =>
    items.map((item, idx) => ({ x: initX(idx), y: initY(idx, item.size) }))
  );
  const [activeIdx, setActiveIdx] = useState(null);

  // dragRef: 드래그 시작 정보 (ref라 re-render 없이 최신값 유지)
  const dragRef = React.useRef(null);
  const movedRef = React.useRef(false);

  const onPointerDown = useCallback((e, idx) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      idx,
      startX: e.clientX,
      startY: e.clientY,
      origX: positions[idx]?.x ?? 0,
      origY: positions[idx]?.y ?? 0,
    };
    movedRef.current = false;
    setActiveIdx(idx);
  }, [positions]);

  const onPointerMove = useCallback((e) => {
    if (!dragRef.current) return;
    const { idx, startX, startY, origX, origY } = dragRef.current;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) movedRef.current = true;
    setPositions(prev => {
      const next = [...prev];
      next[idx] = { x: origX + dx, y: origY + dy };
      return next;
    });
  }, []);

  const onPointerUp = useCallback(() => {
    dragRef.current = null;
    setActiveIdx(null);
  }, []);

  const handleClick = useCallback((ticker) => {
    if (movedRef.current) return;
    onBubbleClick(ticker);
  }, [onBubbleClick]);

  return (
    <div className="rbt-cloud">
      {items.map((item, idx) => {
        const isUp     = item.rankChange > 0;
        const isDown   = item.rankChange < 0;
        const colorCls = isUp ? 'rbt-bbl-up' : isDown ? 'rbt-bbl-down' : 'rbt-bbl-neu';
        const fontSize = item.size >= 74 ? 12 : item.size >= 58 ? 10 : 9;
        const pos      = positions[idx] ?? { x: 0, y: 0 };
        const isDragging = activeIdx === idx;

        return (
          <div
            key={item.ticker}
            className={`rbt-bbl-wrapper ${isDragging ? 'rbt-bbl-dragging' : ''}`}
            style={{
              transform: `translate(${pos.x}px, ${pos.y}px)`,
              animationDelay: `${idx * 55}ms`,
              zIndex: isDragging ? 20 : 1,
            }}
            onPointerDown={(e) => onPointerDown(e, idx)}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            <button
              className={`rbt-bbl ${colorCls}`}
              style={{ width: item.size, height: item.size, fontSize }}
              onClick={() => handleClick(item.ticker)}
            >
              <span className="rbt-bbl-ticker">{item.ticker}</span>
              {item.rankChange !== 0 && (
                <span className={`rbt-bbl-change ${isUp ? 'rbt-arrow-up' : 'rbt-arrow-down'}`}>
                  {isUp ? '▲' : '▼'}{Math.abs(item.rankChange)}
                </span>
              )}
            </button>
          </div>
        );
      })}
    </div>
  );
};

const RedditBuzzBubble = () => {
  // const navigate = useNavigate();
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const trending = await getRedditTrending();
        if (cancelled) return;
        const top = (trending || []).slice(0, MAX_ITEMS);
        if (top.length === 0) { setLoading(false); return; }
        const minM = Math.min(...top.map(t => t.mentions));
        const maxM = Math.max(...top.map(t => t.mentions));
        setItems(top.map(t => ({ ...t, size: computeSize(t.mentions, minM, maxM) })));
      } catch (err) {
        console.error('[RedditBuzzBubble] fetch error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // const handleClick = useCallback(ticker => navigate(`/coinDetail/${ticker}`), [navigate]);
  const handleClick = useCallback(ticker => {}, []);

  return (
    <div className="feature-section">
      <div className="flex items-center gap-2 mb-3">
        <div className="feature-icon rbt-icon">
          <i className="fa-brands fa-reddit-alien" style={{ fontSize: 20, color: '#ff4500' }} />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-bold text-black">Reddit 트렌딩</h2>
            <span className="edu-fun-badge">스낵 콘텐츠</span>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">r/stocks · r/wallstreetbets · 24시간 내 언급량 기준</p>
        </div>
      </div>

      {loading ? (
        <div className="rbt-cloud rbt-cloud-loading">
          {[86, 70, 60, 52, 74, 46, 62, 50].map((s, i) => (
            <div key={i} className="rbt-bbl-skeleton" style={{ width: s, height: s, animationDelay: `${i * 80}ms` }} />
          ))}
        </div>
      ) : items.length > 0 ? (
        <BubbleCloud items={items} onBubbleClick={handleClick} />
      ) : null}

      {!loading && items.length > 0 && (
        <div className="rbt-legend">
          <span className="rbt-legend-item rbt-legend-up">▲ 순위 상승</span>
          <span className="rbt-legend-dot" />
          <span className="rbt-legend-item rbt-legend-down">▼ 순위 하락</span>
          <span className="rbt-legend-dot" />
          <span className="rbt-legend-item rbt-legend-neu">크기 = 언급 수</span>
        </div>
      )}
    </div>
  );
};

export default RedditBuzzBubble;
